import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { buildSimpleNav } from './navModel';

type NavRole = 'owner' | 'admin' | 'worker';

interface MobileNavItem {
  to: string;
  label: string;
  icon: string;
}

interface MoreItem {
  to: string;
  label: string;
  roles?: NavRole[];
  /** Per-tenant feature flag key — item hidden when the flag is explicitly
   * false on the tenant (see TenantContext.hasFeature). */
  feature?: string;
}

const mobileItems: MobileNavItem[] = [
  {
    to: '/',
    label: 'Home',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4',
  },
  {
    to: '/roles',
    label: 'Agents',
    icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  },
  {
    to: '/conversations',
    label: 'Chats',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  },
  {
    to: '/orders',
    label: 'Orders',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  },
  {
    to: '/insights',
    label: 'Stats',
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
];

const STAFF: NavRole[] = ['owner', 'admin'];
const OWNER: NavRole[] = ['owner'];

// Everything the sidebar offers that the 5 tabs don't. Role-gated the same
// way as Sidebar so phone users see exactly what they'd see on desktop.
const moreItems: MoreItem[] = [
  { to: '/insights', label: 'Insights', roles: STAFF },
  { to: '/business-ops', label: 'Business Ops', roles: STAFF },
  { to: '/knowledge', label: 'Knowledge', roles: STAFF },
  { to: '/catalog', label: 'Services & Prices', roles: STAFF },
  { to: '/growth', label: 'Growth Coach', roles: STAFF, feature: 'growth' },
  { to: '/connections', label: 'Connections', roles: OWNER },
  { to: '/team', label: 'Team', roles: STAFF },
  { to: '/settings', label: 'Settings', roles: STAFF },
];

function MobileNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const { user } = useAuth();
  const { hasFeature, isSimpleMode, activeAgentKinds } = useTenant();
  const role = (user?.role === 'operator' ? 'owner' : (user?.role ?? 'worker')) as NavRole;

  // In simple mode the bottom bar collapses to Home; every tailored destination
  // (from the same agent-driven model as the sidebar) lives in the More sheet —
  // which also sidesteps the ungated fixed-tab bar.
  const bottomItems = isSimpleMode ? mobileItems.filter((i) => i.to === '/') : mobileItems;
  const visibleMoreItems = isSimpleMode
    ? buildSimpleNav(activeAgentKinds, hasFeature)
        .flatMap((g) => g.items)
        .filter((i) => i.to !== '/')
    : moreItems.filter(
        (i) => (!i.roles || i.roles.includes(role)) && (!i.feature || hasFeature(i.feature)),
      );

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 glass border-t border-line z-30">
        <div className="flex items-center justify-around py-2">
          {bottomItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                clsx(
                  'flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors',
                  isActive ? 'text-accent' : 'text-muted',
                )
              }
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={item.icon} />
              </svg>
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={() => setMoreOpen((o) => !o)}
            aria-expanded={moreOpen}
            className={clsx(
              'flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors',
              moreOpen ? 'text-accent' : 'text-muted',
            )}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h6v6H4V4zM14 4h6v6h-6V4zM4 14h6v6H4v-6zM14 14h6v6h-6v-6z" />
            </svg>
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {moreOpen && (
        <>
          {/* Backdrop — tap anywhere outside the sheet to dismiss. */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/40"
            onClick={() => setMoreOpen(false)}
          />
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-line rounded-t-2xl max-h-[70vh] overflow-y-auto animate-slide-up">
            <div className="py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
              {visibleMoreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center justify-between px-5 py-3 text-[13px] transition-colors',
                      isActive ? 'text-accent' : 'text-text hover:bg-panel-soft',
                    )
                  }
                >
                  <span className="truncate">{item.label}</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className="text-muted flex-shrink-0"
                  >
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </NavLink>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default MobileNav;
